// Synthesized audio — no asset files needed. Gentle, major-key tones reinforce
// the calming, healing tone. A single shared AudioContext is created lazily on
// the first user gesture (browser autoplay policy).

let ctx: AudioContext | null = null;

function ensureCtx(): AudioContext | null {
  if (typeof window === "undefined") return null;
  if (!ctx) {
    const Ctor = window.AudioContext ?? (window as any).webkitAudioContext;
    if (!Ctor) return null;
    ctx = new Ctor();
  }
  if (ctx.state === "suspended") void ctx.resume();
  return ctx;
}

function tone(freq: number, startOffset: number, duration: number, gainPeak: number): void {
  const ac = ensureCtx();
  if (!ac) return;
  const t0 = ac.currentTime + startOffset;
  const osc = ac.createOscillator();
  const gain = ac.createGain();
  osc.type = "sine";
  osc.frequency.value = freq;
  gain.gain.setValueAtTime(0.0001, t0);
  gain.gain.exponentialRampToValueAtTime(gainPeak, t0 + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);
  osc.connect(gain);
  gain.connect(ac.destination);
  osc.start(t0);
  osc.stop(t0 + duration + 0.05);
}

// Major pentatonic-ish notes (C5 region) — never dissonant.
const NOTES = [523.25, 587.33, 659.25, 783.99, 880.0];

export const AudioFx = {
  /** Call once on a user gesture to unlock audio. */
  unlock(): void {
    ensureCtx();
  },

  /** Soft major chime on a digest — pitch rises slightly with combo index. */
  digestChime(index = 0): void {
    const note = NOTES[Math.min(index, NOTES.length - 1)];
    tone(note, 0, 0.35, 0.12);
    tone(note * 2, 0.0, 0.22, 0.05);
  },

  /** Soft "gulp" the moment an enemy is engulfed (contact), before digestion. */
  engulf(): void {
    tone(220.0, 0, 0.1, 0.11);
    tone(330.0, 0.04, 0.12, 0.06);
  },

  /** Very soft, short tick for an auto-fired shot (fires often → kept quiet). */
  shoot(): void {
    tone(660.0, 0, 0.05, 0.03);
  },

  /** Warm two-note flourish for level clear / revive completion. */
  flourish(): void {
    tone(523.25, 0.0, 0.5, 0.12);
    tone(659.25, 0.12, 0.5, 0.12);
    tone(783.99, 0.24, 0.7, 0.14);
  },

  /** Low, soft pulse used as a breathing guide tone. */
  breathTone(rising: boolean): void {
    tone(rising ? 392.0 : 329.63, 0, 0.6, 0.08);
  },

  /** Subtle negative-but-gentle blip when taking damage. */
  hurt(): void {
    tone(196.0, 0, 0.18, 0.07);
  },
};
